import { useQuery } from '@tanstack/react-query';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
} from 'recharts';
import { get } from '@/api/client';
import { Skeleton } from '@/components/atoms/Skeleton';
import { Badge } from '@/components/atoms/Badge';

/** USD → KRW 환산 범위 — 환율 변동 흡수용 lo/hi (1300~1450).
 *  DashboardPage 와 동일 정책. */
const KRW_LO = 1300;
const KRW_HI = 1450;
function usdToKrwRange(usd: number): string {
  if (!usd || usd <= 0) return '0원';
  const lo = Math.round(usd * KRW_LO);
  const hi = Math.round(usd * KRW_HI);
  if (lo === hi) return `${lo.toLocaleString()}원`;
  return `${lo.toLocaleString()}~${hi.toLocaleString()}원`;
}

interface Summary {
  monthlyLimit: number;
  usage: number;
  calls: number;
  remaining: number;
  percent: number;
  warn: boolean;
  critical: boolean;
  /* 실시간 오늘 사용량 — ticker_ai_analysis 직접 집계 (실제 청구 기준) */
  todayUsage: number;
  todayCalls: number;
  /* 청구액(SDK) / 이론값(CLI · Max 구독) 분리 — 2026-05-29 신설 */
  billing?: {
    actualUsd: {
      month: number;
      today: number;
      breakdown: {
        tagSdkMonth: number;
        tagSdkToday: number;
        manipSdkMonth: number;
        manipSdkToday: number;
      };
    };
    theoreticalUsd: {
      month: number;
      today: number;
      breakdown: {
        basicCliMonth: number;
        basicCliToday: number;
        manipCliMonth: number;
        manipCliToday: number;
      };
      note: string;
    };
  };
  byFeature: {
    basic_month: number;
    manip_month: number;
    manip_cli_month?: number;
    tag_month: number;
    basic_today: number;
    manip_today: number;
    manip_cli_today?: number;
    tag_today: number;
  };
  /* 단가/모델/캐시 정책 — 어드민 표시용 (2026-05 재책정).
     actualAvgUsd: 실측 토큰 기반 건당 평균, actualN: 실측 표본 수 */
  pricing?: {
    basic: { label: string; model: string; costUsd: number; cacheDays?: number; actualAvgUsd?: number | null; actualN?: number };
    manip: { label: string; model: string; costUsd: number; reanalyzeDays?: number; actualAvgUsd?: number | null; actualN?: number };
    tag: { label: string; model: string; costUsd: number; note?: string; actualAvgUsd?: number | null; actualN?: number };
  };
}

interface Daily {
  items: Array<{
    date: string;
    calls: number;
    cost_usd: number;
    by_feature: { basic_analysis?: number; manipulation?: number } | null;
  }>;
}

interface TopTickers {
  items: Array<{
    ticker: string;
    name: string | null;
    analysis_count: number;
    last_analyzed_at: string;
  }>;
}

export function SystemPage() {
  /* 실시간 — 30초 간격 폴링. 페이지 떠나있는 동안엔 재요청 안 함(staleTime+gcTime 기본). */
  const { data: summary, isLoading: sLoading, dataUpdatedAt } = useQuery({
    queryKey: ['admin', 'system', 'ai-cost', 'summary'],
    queryFn: () => get<Summary>('/admin/system/ai-cost/summary'),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
  const { data: daily, isLoading: dLoading } = useQuery({
    queryKey: ['admin', 'system', 'ai-cost', 'daily'],
    queryFn: () => get<Daily>('/admin/system/ai-cost/daily?days=30'),
    refetchInterval: 60_000,
  });
  const { data: top } = useQuery({
    queryKey: ['admin', 'system', 'ai-cost', 'top-tickers'],
    queryFn: () => get<TopTickers>('/admin/system/ai-cost/top-tickers'),
    refetchInterval: 60_000,
  });

  const lastUpdate = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : '-';

  return (
    <div className="w-full">
      <div className="mb-6 flex items-center justify-between gap-2">
        <h1 className="text-2xl font-extrabold">시스템 / AI 비용</h1>
        <span className="text-xs text-[var(--color-text-sub)]">
          실시간 · 마지막 갱신 {lastUpdate} (30초 폴링)
        </span>
      </div>

      {/* 청구 정책 안내 — 2026-05-29 추가 */}
      <section className="mb-6 rounded-xl border border-[var(--color-primary)]/30 bg-[var(--color-primary)]/5 p-4">
        <p className="text-sm font-semibold text-[var(--color-text)]">
          ※ <code className="rounded bg-white px-1 font-mono text-[12px]">claude -p</code>(Max 구독) 호출 비용은 실제 청구되지 않으며 토큰 기반 <strong>이론값</strong>으로 표시됩니다.
        </p>
        <p className="mt-1 text-xs text-[var(--color-text-sub)]">
          실제 API 과금: 어드민 태깅(SDK)과 세력분석 SDK 라우터(데드, 호출 없음)만 해당.
          종목 기본 분석·세력 분석·공시 분석·차트 분석·뉴스 cron·모닝브리핑은 모두 CLI 경로로 결제 0입니다.
        </p>
      </section>

      {/* 청구액 vs 이론값 분리 카드 — 2026-05-29 추가 */}
      {!sLoading && summary?.billing && (
        <section className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* 실제 청구 (SDK) */}
          <div className="rounded-xl border-2 border-[var(--color-rise)]/30 bg-[var(--color-rise)]/5 p-5">
            <div className="mb-2 flex items-center gap-2">
              <h2 className="text-base font-bold text-[var(--color-text)]">실제 청구 예상 (SDK)</h2>
              <Badge variant="fall">실제 결제</Badge>
            </div>
            <div className="text-3xl font-extrabold tabular-nums text-[var(--color-text)]">
              ${summary.billing.actualUsd.month.toFixed(4)}
              <span className="ml-2 text-xs font-medium text-[var(--color-text-sub)]">/월</span>
            </div>
            <div className="mt-1 text-[11px] text-[var(--color-text-sub)]">
              약 {usdToKrwRange(summary.billing.actualUsd.month)}
            </div>
            <ul className="mt-3 space-y-1 text-xs text-[var(--color-text-sub)]">
              <li>· 어드민 태깅 SDK: ${summary.billing.actualUsd.breakdown.tagSdkMonth.toFixed(4)}</li>
              <li>· 세력분석 SDK(데드): ${summary.billing.actualUsd.breakdown.manipSdkMonth.toFixed(4)}</li>
              <li className="pt-1">· 오늘: ${summary.billing.actualUsd.today.toFixed(4)}</li>
            </ul>
          </div>

          {/* 이론값 (CLI · Max 구독) */}
          <div className="rounded-xl border-2 border-[var(--color-primary)]/30 bg-[var(--color-primary)]/5 p-5">
            <div className="mb-2 flex items-center gap-2">
              <h2 className="text-base font-bold text-[var(--color-text)]">이론값 (CLI · Max 구독)</h2>
              <Badge variant="editor">결제 0</Badge>
            </div>
            <div className="text-3xl font-extrabold tabular-nums text-[var(--color-text)]">
              ${summary.billing.theoreticalUsd.month.toFixed(2)}
              <span className="ml-2 text-xs font-medium text-[var(--color-text-sub)]">/월</span>
            </div>
            <div className="mt-1 text-[11px] text-[var(--color-text-sub)]">
              약 {usdToKrwRange(summary.billing.theoreticalUsd.month)} <span className="font-semibold text-[var(--color-primary)]">(미청구)</span>
            </div>
            <ul className="mt-3 space-y-1 text-xs text-[var(--color-text-sub)]">
              <li>· 종목 기본 분석 CLI: ${summary.billing.theoreticalUsd.breakdown.basicCliMonth.toFixed(2)}</li>
              <li>· 세력 분석 CLI: ${summary.billing.theoreticalUsd.breakdown.manipCliMonth.toFixed(4)}</li>
              <li className="pt-1">· 오늘: ${summary.billing.theoreticalUsd.today.toFixed(4)}</li>
            </ul>
            <p className="mt-2 text-[10px] leading-relaxed text-[var(--color-text-sub)]">
              {summary.billing.theoreticalUsd.note}
            </p>
          </div>
        </section>
      )}

      {/* 월 한도 카드 */}
      {sLoading ? (
        <Skeleton className="h-32" />
      ) : (
        <section
          className={`mb-6 rounded-xl border p-5 ${
            summary?.critical
              ? 'border-[var(--color-rise)] bg-[var(--color-rise)]/5'
              : summary?.warn
                ? 'border-[var(--color-warn)] bg-[var(--color-warn-bg)]'
                : 'border-[var(--color-border)] bg-[var(--color-surface)]'
          }`}
        >
          <div className="mb-3 flex items-center gap-3">
            <h2 className="text-lg font-bold">이번 달 한도 vs 실제 청구 (SDK 기준)</h2>
            {summary?.critical && <Badge variant="fall">위험</Badge>}
            {summary?.warn && !summary.critical && <Badge variant="editor">주의</Badge>}
          </div>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <div>
              <div className="text-xs text-[var(--color-text-sub)]">사용</div>
              <div className="mt-1 text-2xl font-bold">${summary?.usage.toFixed(2)}</div>
              <div className="mt-0.5 text-[11px] text-[var(--color-text-sub)]">
                약 {usdToKrwRange(summary?.usage ?? 0)}
              </div>
            </div>
            <div>
              <div className="text-xs text-[var(--color-text-sub)]">한도</div>
              <div className="mt-1 text-2xl font-bold">${summary?.monthlyLimit.toFixed(2)}</div>
              <div className="mt-0.5 text-[11px] text-[var(--color-text-sub)]">
                약 {usdToKrwRange(summary?.monthlyLimit ?? 0)}
              </div>
            </div>
            <div>
              <div className="text-xs text-[var(--color-text-sub)]">잔여</div>
              <div className="mt-1 text-2xl font-bold">${summary?.remaining.toFixed(2)}</div>
              <div className="mt-0.5 text-[11px] text-[var(--color-text-sub)]">
                약 {usdToKrwRange(summary?.remaining ?? 0)}
              </div>
            </div>
            <div>
              <div className="text-xs text-[var(--color-text-sub)]">호출 수</div>
              <div className="mt-1 text-2xl font-bold">{summary?.calls ?? 0}</div>
            </div>
          </div>
          <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-[var(--color-border)]">
            <div
              className={`h-full ${
                summary?.critical
                  ? 'bg-[var(--color-rise)]'
                  : summary?.warn
                    ? 'bg-[var(--color-warn)]'
                    : 'bg-[var(--color-primary)]'
              }`}
              style={{ width: `${Math.min(100, summary?.percent ?? 0)}%` }}
            />
          </div>
          <p className="mt-2 text-xs text-[var(--color-text-sub)]">
            ※ 사용/잔여는 <strong>실제 청구(SDK)</strong> 기준 — 태깅 + 세력분석 SDK 실측 합산이에요.
            한도는 환경변수 <code className="rounded bg-[var(--color-bg)] px-1">ANTHROPIC_MONTHLY_LIMIT</code> 값.
            CLI(claude -p) 호출의 이론값은 상단 카드에서 별도 확인하세요.
          </p>
        </section>
      )}

      {/* 오늘 실시간 호출 건수 (청구 여부 무관, 활동 모니터링용) */}
      {!sLoading && summary && (
        <section className="mb-6 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5">
          <h2 className="mb-3 text-lg font-bold">오늘 호출 건수 (실시간)</h2>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
            <div>
              <div className="text-xs text-[var(--color-text-sub)]">기본 분석 (CLI)</div>
              <div className="mt-1 text-2xl font-bold">{summary.byFeature.basic_today}</div>
              <div className="mt-0.5 text-[10px] text-[var(--color-primary)]">결제 0</div>
            </div>
            <div>
              <div className="text-xs text-[var(--color-text-sub)]">세력 CLI</div>
              <div className="mt-1 text-2xl font-bold">{summary.byFeature.manip_cli_today ?? 0}</div>
              <div className="mt-0.5 text-[10px] text-[var(--color-primary)]">결제 0</div>
            </div>
            <div>
              <div className="text-xs text-[var(--color-text-sub)]">세력 SDK (데드)</div>
              <div className="mt-1 text-2xl font-bold">{summary.byFeature.manip_today}</div>
              <div className="mt-0.5 text-[10px] text-[var(--color-rise)]">실제 결제</div>
            </div>
            <div>
              <div className="text-xs text-[var(--color-text-sub)]">태깅 SDK</div>
              <div className="mt-1 text-2xl font-bold">{summary.byFeature.tag_today}</div>
              <div className="mt-0.5 text-[10px] text-[var(--color-rise)]">실제 결제</div>
            </div>
            <div>
              <div className="text-xs text-[var(--color-text-sub)]">오늘 청구액</div>
              <div className="mt-1 text-2xl font-bold">${summary.todayUsage.toFixed(4)}</div>
              <div className="mt-0.5 text-[11px] text-[var(--color-text-sub)]">
                약 {usdToKrwRange(summary.todayUsage)}
              </div>
            </div>
          </div>
          <p className="mt-2 text-xs text-[var(--color-text-sub)]">
            CLI 호출은 Max 구독으로 실제 결제 없음. SDK 호출만 Anthropic API 청구 발생.
            기본·세력분석은 ticker_ai_analysis, 태깅은 ticker_ai_tag_meta 직접 집계.
          </p>
        </section>
      )}

      {/* 단가 / 모델 / 캐시 정책 (2026-05 재책정) */}
      {!sLoading && summary?.pricing && (
        <section className="mb-6 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5">
          <h2 className="mb-3 text-lg font-bold">기능별 단가 · 모델</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border)] text-left text-xs text-[var(--color-text-sub)]">
                  <th className="p-2">기능</th>
                  <th className="p-2">모델</th>
                  <th className="p-2 text-right">추정 단가</th>
                  <th className="p-2 text-right">실측 평균 · 최근 30일 (표본)</th>
                  <th className="p-2">캐시 / 재분석 주기</th>
                </tr>
              </thead>
              <tbody>
                {[
                  {
                    ...summary.pricing.basic,
                    cycle: summary.pricing.basic.cacheDays
                      ? `${summary.pricing.basic.cacheDays}일 캐시`
                      : '-',
                  },
                  {
                    ...summary.pricing.manip,
                    cycle: summary.pricing.manip.reanalyzeDays
                      ? `${summary.pricing.manip.reanalyzeDays}일 재분석`
                      : '-',
                  },
                  {
                    ...summary.pricing.tag,
                    cycle: summary.pricing.tag.note ?? '-',
                  },
                ].map((p) => {
                  const actual = p.actualAvgUsd;
                  const n = p.actualN ?? 0;
                  const diffPct =
                    actual != null && p.costUsd > 0
                      ? Math.round(((actual - p.costUsd) / p.costUsd) * 100)
                      : null;
                  return (
                    <tr key={p.label} className="border-b border-[var(--color-border)] last:border-0">
                      <td className="p-2 font-medium">{p.label}</td>
                      <td className="p-2">{p.model}</td>
                      <td className="p-2 text-right tabular-nums">
                        ${p.costUsd.toFixed(4)}
                        <span className="ml-1 text-[11px] text-[var(--color-text-sub)]">
                          ({usdToKrwRange(p.costUsd)})
                        </span>
                      </td>
                      <td className="p-2 text-right tabular-nums">
                        {actual != null && n > 0 ? (
                          <>
                            <span className="font-semibold">${actual.toFixed(4)}</span>
                            <span className="ml-1 text-[11px] text-[var(--color-text-sub)]">
                              ({n}건
                              {diffPct != null && (
                                <span className={diffPct > 0 ? 'text-[var(--color-rise)]' : 'text-[var(--color-fall)]'}>
                                  {' '}추정 대비 {diffPct > 0 ? '+' : ''}{diffPct}%
                                </span>
                              )}
                              )
                            </span>
                          </>
                        ) : (
                          <span className="text-[11px] text-[var(--color-text-sub)]">실측 누적 전</span>
                        )}
                      </td>
                      <td className="p-2 text-[var(--color-text-sub)]">{p.cycle}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-xs text-[var(--color-text-sub)]">
            ※ 실측 평균은 각 호출 시 Anthropic 응답 토큰(msg.usage)으로 계산해 저장한 값이며, 최근 30일 표본 기준입니다.
            추정 대비 ±20% 이상 괴리가 지속되면 <code className="rounded bg-[var(--color-bg)] px-1">.env</code>의
            <code className="ml-1 rounded bg-[var(--color-bg)] px-1">AI_COST_ESTIMATE_BASIC_USD</code> /
            <code className="ml-1 rounded bg-[var(--color-bg)] px-1">AI_COST_ESTIMATE_MANIP_USD</code> 값을 실측 평균에 맞춰 조정하고
            <code className="ml-1 rounded bg-[var(--color-bg)] px-1">pm2 restart sisegonggan --update-env</code>로 재적용하세요.
          </p>
        </section>
      )}

      {/* 일별 비용 차트 */}
      <section className="mb-6 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
        <h2 className="mb-3 text-lg font-bold">최근 30일 일별 비용</h2>
        {dLoading ? (
          <Skeleton className="h-72" />
        ) : (
          <div style={{ width: '100%', height: 280 }}>
            <ResponsiveContainer>
              <BarChart data={daily?.items ?? []}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip
                  contentStyle={{
                    background: 'var(--color-surface)',
                    border: '1px solid var(--color-border)',
                    fontSize: 12,
                  }}
                />
                <Bar dataKey="cost_usd" fill="#ea580c" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </section>

      {/* 분석 많이 받은 종목 Top 20 */}
      <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
        <h2 className="mb-3 text-lg font-bold">AI 분석 받은 종목 Top 20</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--color-bg)] text-left">
              <tr>
                <th className="p-2">티커</th>
                <th className="p-2">종목명</th>
                <th className="p-2 text-right">분석 횟수</th>
                <th className="p-2 text-right">최근 분석</th>
              </tr>
            </thead>
            <tbody>
              {top?.items.map((t) => (
                <tr key={t.ticker} className="border-t border-[var(--color-border)]">
                  <td className="p-2 font-mono">{t.ticker}</td>
                  <td className="p-2">{t.name ?? '-'}</td>
                  <td className="p-2 text-right">{t.analysis_count}</td>
                  <td className="p-2 text-right text-xs text-[var(--color-text-sub)]">
                    {t.last_analyzed_at
                      ? new Date(t.last_analyzed_at).toISOString().slice(0, 16).replace('T', ' ')
                      : '-'}
                  </td>
                </tr>
              ))}
              {top?.items.length === 0 && (
                <tr>
                  <td colSpan={4} className="p-6 text-center text-[var(--color-text-sub)]">
                    데이터가 없어요.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
